import { InjectionToken, Provider } from '@angular/core';
import { ts } from '@core/utilities';
export { Toast } from './toast';

/** ARIA live-region role applied to the toast host. */
export type ToastRole = 'alert' | 'warning' | 'info' | 'success';

/** Native popover mode. `auto` = light-dismiss + closes other `auto` popovers; `manual` = explicit dismissal only. */
export type ToastPopover = 'manual' | 'auto' | '';

/** Reveal/dismiss animation. Exit mirrors entry. */
export type ToastAnimation =
  'none' | 'fade' | 'scale' | 'slide-from-bottom' | 'slide-from-top' | 'slide-from-start' | 'slide-from-end';

/** When supplied, sets the toast's duration before being automatically dismissed */
export type ToastAutoDismissDuration = `${number}${'s' | 'ms'}`;

/** X */
export type ToastFontSizeBase = `${number}${'em' | 'rem' | 'px'}`;

/** Positions in reference to the viewport, by which the Toast(s) are placed/stacked (x-axis - y-axis) */
export type ToastPlacement =
  | 'start-top'
  | 'start-center'
  | 'start-bottom'
  | 'end-top'
  | 'end-center'
  | 'end-bottom'
  | 'center-top'
  | 'center-bottom'
  | 'center';

interface ToastSupportBase {
  shouldShowServiceDesk: boolean;
  shouldShowHelpCenter: boolean;
}
export type ToastSupport = ts.RequireAtLeastOne<ToastSupportBase>;

export interface ToastOptions {
  /** Plain string. Inline text-level elements (<em>, <strong>, <code>, …) are allowed; anything else is unwrapped. */
  title: string;
  /** Plain string. Same inline-element rules as `title`. */
  body: string;
  role?: ToastRole;
  popover?: ToastPopover;
  animation?: ToastAnimation;
  classNames?: string[] | string | null;
  autoDismiss?: ToastAutoDismissDuration | false;
  placement?: ToastPlacement;
  support?: ToastSupport;
}

export type ToastDefaults = Required<
  Pick<ToastOptions, 'role' | 'popover' | 'animation' | 'classNames' | 'autoDismiss' | 'placement'>
>;

/** Shorthand options for `Toast.status()` / `Toast.alert()`. */
export type ToastShorthandOptions = Omit<ToastOptions, 'title' | 'body' | 'role'>;

export interface ToastRef {
  readonly id: string;
  /** Resolves once the toast has closed, finished any exit animation, and been removed from the DOM. */
  readonly closed: Promise<void>;
  dismiss(): void;
}

export const DEFAULT_TOAST_OPTIONS: ToastDefaults = {
  role: 'info',
  popover: 'manual',
  animation: 'none',
  classNames: null,
  autoDismiss: false,
  placement: 'end-top'
};

export const TOAST_DEFAULTS = new InjectionToken<ToastDefaults>('TOAST_DEFAULTS', {
  providedIn: 'root',
  factory: (): ToastDefaults => DEFAULT_TOAST_OPTIONS
});

/** Optional app-wide override of the defaults, e.g. `provideToastDefaults({ animation: 'fade' })` in app.config.ts. */
export function provideToastDefaults(defaults: Partial<ToastDefaults>): Provider {
  return { provide: TOAST_DEFAULTS, useValue: { ...DEFAULT_TOAST_OPTIONS, ...defaults } };
}
